/**
 * legacy-analytics-events.js
 *
 * Restores custom GTM events that existed on the previous (Nyla) site and
 * that Shopify's own tracking does not cover: newsletter signup, general
 * contact-form submissions, and custom/made-to-order ring inquiries.
 *
 * All three source forms are intercepted (fully or partially) by the
 * hand-built AJAX handler in layout/theme.liquid, which registers its
 * listener on `window` in the capture phase from a synchronous <script> in
 * <head> -- earlier in both node position and registration time than
 * anything a deferred module script like this one can attach. It also
 * calls stopImmediatePropagation() on the real submit event, and for the
 * newsletter form submits via a programmatic .submit() call that never
 * dispatches a 'submit' event at all. A 'submit' listener can never
 * observe any of this. Instead, each form below is tracked by watching the
 * status element that script already writes its result into, reading
 * field values live where the value survives (nothing navigates away or
 * gets reset before we read it) and via sessionStorage where it doesn't
 * (the AJAX contact handler calls form.reset() right after rendering
 * success, and a native full-page reload -- the fallback path when JS
 * doesn't intercept a form at all -- loses in-memory state completely).
 */

const pushToDataLayer = (payload) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
};

const readAndClear = (key) => {
  try {
    const value = sessionStorage.getItem(key);
    sessionStorage.removeItem(key);
    return value || "";
  } catch {
    return "";
  }
};

const cache = (key, value) => {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* sessionStorage unavailable (private browsing, etc.) -- fails silently */
  }
};

/* --- Newsletter (blocks/email-signup.liquid) --- */

const STORAGE_KEY_NEWSLETTER_EMAIL = "nylaTrack:newsletterEmail";

const watchNewsletterForm = (form) => {
  const emailInput = form.querySelector('input[name="contact[email]"]');
  emailInput?.addEventListener("input", () => cache(STORAGE_KEY_NEWSLETTER_EMAIL, emailInput.value));

  const location = form.closest(".email-signup-block")?.dataset.formLocation || "unknown";

  const fireSuccess = () => {
    pushToDataLayer({
      event: "gtmEvent",
      eventCategory: "account",
      eventAction: "newsletter signup",
      eventLabel: location,
      customerEmail: readAndClear(STORAGE_KEY_NEWSLETTER_EMAIL),
    });
  };

  const status = form.querySelector("[data-newsletter-form-status]");
  if (status && !status.dataset.legacyTrackerAttached) {
    status.dataset.legacyTrackerAttached = "true";
    const observer = new MutationObserver(() => {
      if (status.hidden) return;
      const succeeded = !status.classList.contains("email-signup__message--error");
      observer.disconnect();
      if (succeeded) fireSuccess();
    });
    observer.observe(status, { attributes: true, attributeFilter: ["hidden", "class"] });
  }

  /* Fallback: JS didn't intercept the submit and the form did a real
     full-page reload -- the success block is already in the loaded HTML. */
  if (form.querySelector("[data-newsletter-success]")) {
    fireSuccess();
  }
};

document.querySelectorAll(".email-signup__form").forEach(watchNewsletterForm);

/* --- General contact form (blocks/contact-us-section-form.liquid) --- */

const STORAGE_KEY_CONTACT_SUBJECT = "nylaTrack:contactSubject";
const CUSTOM_SUBJECT = "Custom";

const watchContactForm = (form) => {
  const subjectSelect = form.querySelector('select[name="contact[Subject]"]');
  subjectSelect?.addEventListener("change", () => cache(STORAGE_KEY_CONTACT_SUBJECT, subjectSelect.value));

  const status = form.querySelector("[data-contact-us-form-status]");
  if (!status || status.dataset.legacyTrackerAttached) return;
  status.dataset.legacyTrackerAttached = "true";

  const checkAndFire = () => {
    if (status.hidden) return false;
    if (!status.querySelector(".contact-us-section-form__status-message--success")) return false;

    const subject = readAndClear(STORAGE_KEY_CONTACT_SUBJECT);
    pushToDataLayer({
      event: subject === CUSTOM_SUBJECT ? "custom_ring_form_submission" : "contact_form_submission",
    });
    return true;
  };

  /* Fallback: native full-page reload, success already rendered on load. */
  if (checkAndFire()) return;

  /* Primary path: the AJAX handler updates this same element in place and
     resets the form right after -- we've already cached the Subject above,
     so the reset doesn't affect us. */
  const observer = new MutationObserver(() => {
    if (checkAndFire()) observer.disconnect();
  });
  observer.observe(status, { attributes: true, attributeFilter: ["hidden"], childList: true, subtree: true });
};

document.querySelectorAll(".contact-us-section-form__form").forEach(watchContactForm);

/* --- Custom/made-to-order ring form (blocks/ooak-form.liquid) ---
   Not matched by the AJAX handler at all, so submitting it is a plain native
   form post back to the same product page. The theme's page-transition
   system can turn what looks like a full reload into a soft, in-place DOM
   swap though, so the success marker can appear *after* this script has
   already run once -- check immediately for a genuine hard reload, and also
   watch for the marker being inserted later for the soft-transition case.
   Reported as its own event, separate from the contact form's "Custom"
   subject (custom_ring_form_submission) -- no branching needed. */

const checkOoakSuccess = () => {
  if (!document.querySelector("[data-ooak-form-success]")) return false;
  pushToDataLayer({ event: "ooak_form_submission" });
  return true;
};

if (!checkOoakSuccess()) {
  const ooakObserver = new MutationObserver(() => {
    if (checkOoakSuccess()) ooakObserver.disconnect();
  });
  ooakObserver.observe(document.body, { childList: true, subtree: true });
}
